import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import {
    BasicInfoSection,
    BrandingSection,
    HeroSection,
    LocationContactSection,
    GallerySection,
    DealsSection,
    SocialFooterSection,
    ModalSection,
    SaveDialog,
} from './components';
import type {
    MicrositeFormData,
    HeroImage,
    HeroTag,
    GalleryImage,
    DealItem,
    TimingSlot,
} from './types/microsite.types';
import MicrositeTransformService from '../../utils/micrositeTransform';
import { transformApiToFormData } from '../../utils/apiToFormTransform';
import apiClient from '../../utils/api/ApiClient';
import { API_ENDPOINTS, MICROSITE_STATUS } from '../../constants/api.constants';
import { useAppDispatch } from '../../store';
import {
    fetchMicrositeById,
    useCurrentMicrosite,
    useMicrositeLoading,
    clearCurrentMicrosite,
} from '../../store/slices/micrositeSlice';

type SaveStatus = 'idle' | 'loading' | 'success' | 'error';
type MicrositeStatus = 'Draft' | 'Published' | 'Inactive';
type SuccessType = 'publish' | 'inactive' | 'live';

/**
 * CreateMicrosite page - Modular form for creating/editing microsites
 */
export default function CreateMicrosite() {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { micrositeID } = useParams<{
        micrositeID?: string;
    }>();
    const isEditMode = Boolean(micrositeID);

    // Redux state
    const currentMicrosite = useCurrentMicrosite();
    const micrositeLoading = useMicrositeLoading();


    const [formData, setFormData] = useState<MicrositeFormData>({
        micrositeType: 'brand',
        storeBrandID: '',
        storeBrandSlug: '',
    });
    const [micrositeStatus, setMicrositeStatus] = useState<MicrositeStatus>(MICROSITE_STATUS.DRAFT);
    const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
    const [successType, setSuccessType] = useState<SuccessType>('publish');
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string>('');


    // Load microsite data in edit mode
    useEffect(() => {
        if (isEditMode && micrositeID) {
            // Fetch microsite data using ID
            dispatch(fetchMicrositeById(micrositeID));
        }

        // Cleanup on unmount
        return () => {
            dispatch(clearCurrentMicrosite());
        };
    }, [isEditMode, micrositeID, dispatch]);

    // Transform and populate form data when microsite is loaded
    useEffect(() => {
        if (currentMicrosite && isEditMode) {
            const transformedData = transformApiToFormData(currentMicrosite);
            setFormData(transformedData);

            // Set microsite status directly from API (no conversion)
            setMicrositeStatus(currentMicrosite.status);
        }
    }, [currentMicrosite, isEditMode]);

    // Stable update functions using useCallback to prevent infinite re-renders
    const updateField = useCallback(
        <K extends keyof MicrositeFormData>(field: K, value: MicrositeFormData[K]) => {
            setFormData((prev) => ({ ...prev, [field]: value }));
        },
        []
    );

    // ============================================
    // BasicInfoSection Handlers
    // ============================================
    const handleMicrositeTypeChange = useCallback(
        (type: MicrositeFormData['micrositeType']) => {
            updateField('micrositeType', type);
        },
        [updateField]
    );

    const handleStoreBrandIDChange = useCallback(
        (id: string) => {
            updateField('storeBrandID', id);
        },
        [updateField]
    );

    const handleStoreBrandSlugChange = useCallback(
        (slug: string) => {
            updateField('storeBrandSlug', slug);
        },
        [updateField]
    );

    const handleStoreIDChange = useCallback(
        (id: string) => {
            updateField('storeID', id);
        },
        [updateField]
    );

    const handleStoreSlugChange = useCallback(
        (slug: string) => {
            updateField('storeSlug', slug);
        },
        [updateField]
    );

    // ============================================
    // BrandingSection Handlers
    // ============================================
    const handleTaglineChange = useCallback(
        (value: string) => {
            updateField('tagline', value);
        },
        [updateField]
    );

    const handleLogoChange = useCallback(
        (file: File | null) => {
            updateField('logo', file || undefined);
        },
        [updateField]
    );

    // Hero Banner (text content - was previously in Hero)
    const handleHeroBannerTitleChange = useCallback((value: string) => {
        setFormData((prev) => ({
            ...prev,
            heroBanner: { ...prev.heroBanner, title: value },
        }));
    }, []);

    const handleHeroBannerSubtitleChange = useCallback((value: string) => {
        setFormData((prev) => ({
            ...prev,
            heroBanner: { ...prev.heroBanner, subtitle: value },
        }));
    }, []);

    const handleHeroBannerHighlightLabelChange = useCallback((value: string) => {
        setFormData((prev) => ({
            ...prev,
            heroBanner: { ...prev.heroBanner, highlightLabel: value },
        }));
    }, []);

    const handleHeroBannerCtaTextChange = useCallback((value: string) => {
        setFormData((prev) => ({
            ...prev,
            heroBanner: { ...prev.heroBanner, ctaText: value },
        }));
    }, []);

    const handleHeroBannerRedirectionUrlChange = useCallback((value: string) => {
        setFormData((prev) => ({
            ...prev,
            heroBanner: { ...prev.heroBanner, redirectionUrl: value },
        }));
    }, []);

    // Meta fields
    const handleMetaTitleChange = useCallback((value: string) => {
        setFormData((prev) => ({ ...prev, metaData: { ...prev.metaData, title: value } }));
    }, []);

    const handleMetaDescriptionChange = useCallback((value: string) => {
        setFormData((prev) => ({ ...prev, metaData: { ...prev.metaData, description: value } }));
    }, []);

    const handleMetaKeywordsChange = useCallback((keywords: string[]) => {
        setFormData((prev) => ({ ...prev, metaData: { ...prev.metaData, keywords } }));
    }, []);

    const handleOgImageChange = useCallback((file: File | null) => {
        setFormData((prev) => ({
            ...prev,
            metaData: { ...prev.metaData, ogImage: file || undefined },
        }));
    }, []);

    // ============================================
    // HeroSection Handlers (NEW - images + tags)
    // ============================================
    const handleHeroImagesChange = useCallback((images: HeroImage[]) => {
        setFormData((prev) => ({
            ...prev,
            hero: { ...prev.hero, imageUrls: images },
        }));
    }, []);

    const handleHeroTagsChange = useCallback((tags: HeroTag[]) => {
        setFormData((prev) => ({
            ...prev,
            hero: { ...prev.hero, tags },
        }));
    }, []);

    // ============================================
    // LocationContactSection Handlers
    // ============================================
    const handleHolidayInfoChange = useCallback((value: string) => {
        setFormData((prev) => ({ ...prev, location: { ...prev.location, holidayInfo: value } }));
    }, []);

    const handleLatitudeChange = useCallback((value: number) => {
        setFormData((prev) => ({ ...prev, geoLocation: { ...prev.geoLocation, latitude: value } }));
    }, []);

    const handleLongitudeChange = useCallback((value: number) => {
        setFormData((prev) => ({
            ...prev,
            geoLocation: { ...prev.geoLocation, longitude: value },
        }));
    }, []);

    const handleGeoLocationValidityChange = useCallback((date: Date | null) => {
        setFormData((prev) => ({
            ...prev,
            geoLocation: { ...prev.geoLocation, validity: date || undefined },
        }));
    }, []);

    const handleStorePhoneChange = useCallback((value: string) => {
        setFormData((prev) => ({
            ...prev,
            storeOperationsConfig: { ...prev.storeOperationsConfig, phone: value },
        }));
    }, []);

    const handleStoreWhatsappChange = useCallback((value: string) => {
        setFormData((prev) => ({
            ...prev,
            storeOperationsConfig: { ...prev.storeOperationsConfig, whatsappNumber: value },
        }));
    }, []);

    const handleTimingsChange = useCallback((timings: TimingSlot[]) => {
        setFormData((prev) => ({
            ...prev,
            storeOperationsConfig: { ...prev.storeOperationsConfig, timings },
        }));
    }, []);

    const handleBrandPhoneChange = useCallback((value: string) => {
        setFormData((prev) => ({
            ...prev,
            storeBrandOperationsConfig: { ...prev.storeBrandOperationsConfig, phone: value },
        }));
    }, []);

    const handleBrandWhatsappChange = useCallback((value: string) => {
        setFormData((prev) => ({
            ...prev,
            storeBrandOperationsConfig: {
                ...prev.storeBrandOperationsConfig,
                whatsappNumber: value,
            },
        }));
    }, []);

    // ============================================
    // GallerySection Handlers (UPDATED - with alt text)
    // ============================================
    const handleGalleryChange = useCallback((images: GalleryImage[]) => {
        setFormData((prev) => ({
            ...prev,
            gallery: { imageUrls: images },
        }));
    }, []);

    // ============================================
    // DealsSection Handlers (UPDATED - new structure)
    // ============================================
    const handleDealsEnabledChange = useCallback((enabled: boolean) => {
        setFormData((prev) => ({ ...prev, deals: enabled ? { items: [] } : undefined }));
    }, []);

    const handleDealsChange = useCallback((items: DealItem[]) => {
        setFormData((prev) => ({ ...prev, deals: { items } }));
    }, []);

    // ============================================
    // SocialFooterSection Handlers (UPDATED - username and followers only)
    // ============================================
    const handleFacebookUsernameChange = useCallback((value: string) => {
        setFormData((prev) => ({
            ...prev,
            social: {
                ...prev.social,
                facebook: { ...prev.social?.facebook, username: value },
            },
        }));
    }, []);

    const handleFacebookFollowersChange = useCallback((value: number) => {
        setFormData((prev) => ({
            ...prev,
            social: {
                ...prev.social,
                facebook: { ...prev.social?.facebook, followers: value },
            },
        }));
    }, []);

    const handleInstagramUsernameChange = useCallback((value: string) => {
        setFormData((prev) => ({
            ...prev,
            social: {
                ...prev.social,
                instagram: { ...prev.social?.instagram, username: value },
            },
        }));
    }, []);

    const handleInstagramFollowersChange = useCallback((value: number) => {
        setFormData((prev) => ({
            ...prev,
            social: {
                ...prev.social,
                instagram: { ...prev.social?.instagram, followers: value },
            },
        }));
    }, []);

    const handleLicenseNumberChange = useCallback((value: string) => {
        setFormData((prev) => ({ ...prev, footer: { ...prev.footer, licenseNumber: value } }));
    }, []);

    const handleMinimumLegalDrinkingAgeChange = useCallback((value: number) => {
        setFormData((prev) => ({
            ...prev,
            footer: { ...prev.footer, minimumLegalDrinkingAge: value },
        }));
    }, []);

    // ============================================
    // ModalSection Handlers (UPDATED - new structure)
    // ============================================
    const handleModalEnabledChange = useCallback((enabled: boolean) => {
        if (!enabled) {
            setFormData((prev) => ({ ...prev, modal: undefined }));
        } else {
            setFormData((prev) => ({ ...prev, modal: {} }));
        }
    }, []);

    const handleModalHeaderBadgeChange = useCallback((value: string) => {
        setFormData((prev) => ({
            ...prev,
            modal: { ...prev.modal, headerBadge: value },
        }));
    }, []);

    const handleModalHeaderTitleChange = useCallback((value: string) => {
        setFormData((prev) => ({
            ...prev,
            modal: { ...prev.modal, headerTitle: value },
        }));
    }, []);

    const handleModalHeaderSubtitleChange = useCallback((value: string) => {
        setFormData((prev) => ({
            ...prev,
            modal: { ...prev.modal, headerSubtitle: value },
        }));
    }, []);

    const handleModalPrimaryButtonTextChange = useCallback((value: string) => {
        setFormData((prev) => ({
            ...prev,
            modal: { ...prev.modal, primaryButtonText: value },
        }));
    }, []);

    const handleModalPrimaryButtonInfoChange = useCallback((value: string) => {
        setFormData((prev) => ({
            ...prev,
            modal: { ...prev.modal, primaryButtonInfo: value },
        }));
    }, []);

    const handleModalFooterNoteChange = useCallback((value: string) => {
        setFormData((prev) => ({
            ...prev,
            modal: { ...prev.modal, footerNote: value },
        }));
    }, []);

    // ============================================
    // Form Actions
    // ============================================
    const handleSaveDraft = async () => {
        setSaveStatus('loading');
        setSuccessType('publish');
        setIsDialogOpen(true);
        setErrorMessage('');

        try {
            // Validate required fields
            if (!formData.storeBrandID || !formData.storeBrandSlug) {
                throw new Error('Store Brand is required');
            }

            if (formData.micrositeType === 'store' && (!formData.storeID || !formData.storeSlug)) {
                throw new Error('Store selection is required for store-specific microsites');
            }

            // Transform form data to API payload (includes image uploads)
            const payload = await MicrositeTransformService.transformToApiPayload(
                formData,
                'saveDraft'
            );

            // Save as draft
            const response = await apiClient.post<{ data: { status: string } }>(
                API_ENDPOINTS.MICROSITE.SAVE,
                payload,
                { headers: { 'X-Store-Brand-Id': formData.storeBrandID } }
            );

            // Update status from API response
            if (response.data?.status) {
                setMicrositeStatus(response.data.status as MicrositeStatus);
            }

            setSaveStatus('success');

            // Navigate to microsites list after 1.5 seconds
            setTimeout(() => {
                navigate('/microsites');
            }, 1500);
        } catch (error) {
            setSaveStatus('error');
            setErrorMessage((error as Error)?.message || 'Failed to save draft');
            console.error('Save draft error:', error);
        }
    };

    const handlePublish = async () => {
        setSaveStatus('loading');
        setSuccessType('publish');
        setIsDialogOpen(true);
        setErrorMessage('');

        try {
            // Validate required fields
            if (!formData.storeBrandID || !formData.storeBrandSlug) {
                throw new Error('Store Brand is required');
            }

            if (formData.micrositeType === 'store' && (!formData.storeID || !formData.storeSlug)) {
                throw new Error('Store selection is required for store-specific microsites');
            }

            // Basic content validation
            if (!formData.tagline) {
                throw new Error('Tagline is required');
            }

            if (
                formData.footer &&
                (!formData.footer.licenseNumber || !formData.footer.minimumLegalDrinkingAge)
            ) {
                throw new Error('License Number and Minimum Legal Drinking Age are required');
            }

            // Transform form data to API payload (includes image uploads)
            const payload = await MicrositeTransformService.transformToApiPayload(
                formData,
                'publish'
            );

            // Publish microsite
            const response = await apiClient.post<{ data: { status: string } }>(
                API_ENDPOINTS.MICROSITE.SAVE,
                payload,
                { headers: { 'X-Store-Brand-Id': formData.storeBrandID } }
            );

            // Update status from API response
            if (response.data?.status) {
                setMicrositeStatus(response.data.status as MicrositeStatus);
            }

            setSaveStatus('success');

            // Navigate to microsites list after 1.5 seconds
            setTimeout(() => {
                navigate('/microsites');
            }, 1500);
        } catch (error) {
            setSaveStatus('error');
            setErrorMessage((error as Error)?.message || 'Failed to publish microsite');
            console.error('Publish error:', error);
        }
    };

    const handleDialogClose = () => {
        setIsDialogOpen(false);
        if (saveStatus === 'success') {
            navigate('/microsites');
        }
        setTimeout(() => setSaveStatus('idle'), 300);
    };

    const handleCancel = () => {
        navigate('/microsites');
    };

    const handleToggleStatus = async () => {
        if (!formData.micrositeID) {
            setErrorMessage('Microsite ID is missing');
            return;
        }

        const newStatus: MicrositeStatus =
            micrositeStatus === MICROSITE_STATUS.PUBLISHED
                ? MICROSITE_STATUS.INACTIVE
                : MICROSITE_STATUS.PUBLISHED;

        setSaveStatus('loading');
        setSuccessType(newStatus === MICROSITE_STATUS.INACTIVE ? 'inactive' : 'live');
        setIsDialogOpen(true);
        setErrorMessage('');

        try {
            await apiClient.patch(
                API_ENDPOINTS.MICROSITE.STATUS,
                { micrositeID: formData.micrositeID, status: newStatus },
                { headers: { 'X-Store-Brand-Id': formData.storeBrandID } }
            );

            setMicrositeStatus(newStatus);
            setSaveStatus('success');
        } catch (error) {
            setSaveStatus('error');
            setErrorMessage((error as Error).message || 'Failed to update microsite status');
        }
    };

    // Show loading state in edit mode while fetching data
    if (isEditMode && micrositeLoading === 'loading') {
        return (
            <div className="w-full flex items-center justify-center py-20">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[#6e64f0] mx-auto mb-4"></div>
                    <p className="text-[#64748b] font-medium">Loading microsite data...</p>
                </div>
            </div>
        );
    }

    // Show error state if fetch failed
    if (isEditMode && micrositeLoading === 'error') {
        return (
            <div className="w-full flex items-center justify-center py-20">
                <div className="text-center max-w-md">
                    <div className="w-16 h-16 rounded-full bg-[#fee2e2] flex items-center justify-center mx-auto mb-4">
                        <svg
                            className="w-8 h-8 text-[#ef4444]"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                        </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-[#1e293b] mb-2">
                        Failed to Load Microsite
                    </h3>
                    <p className="text-[#64748b] mb-6">
                        The microsite could not be loaded. It may not exist or you may not have
                        permission to view it.
                    </p>
                    <button
                        onClick={() => navigate('/microsites')}
                        className="px-6 py-2.5 bg-[#6e64f0] text-white rounded-lg hover:bg-[#5c52e0] transition-colors font-medium"
                    >
                        Back to Microsites
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full space-y-6">
            {/* Header */}
            <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2 text-sm text-[#64748b]">
                    <Link to="/dashboard" className="hover:text-[#6e64f0] transition-colors">
                        Dashboard
                    </Link>
                    <span>/</span>
                    <Link to="/microsites" className="hover:text-[#6e64f0] transition-colors">
                        Microsites
                    </Link>
                    <span>/</span>
                    <span className="text-[#1e293b] font-medium">
                        {isEditMode ? 'Edit Microsite' : 'Create New'}
                    </span>
                </div>
                <div className="flex items-center justify-between">
                    <div>
                        <h2 className="text-4xl font-bold text-[#1e293b]">
                            {isEditMode ? 'Edit Microsite' : 'Create New Microsite'}
                        </h2>
                        <p className="text-lg text-[#64748b] mt-2">
                            {isEditMode
                                ? 'Update your microsite configuration'
                                : 'Fill in the details below to create a new microsite'}
                        </p>
                    </div>
                    {isEditMode && (
                        <div className="flex items-center gap-3">
                            <span
                                className={`px-3 py-1.5 rounded-full text-sm font-medium ${
                                    micrositeStatus === MICROSITE_STATUS.PUBLISHED
                                        ? 'bg-[#dcfce7] text-[#10b981]'
                                        : micrositeStatus === MICROSITE_STATUS.DRAFT
                                          ? 'bg-[#fef3c7] text-[#f59e0b]'
                                          : 'bg-[#f1f5f9] text-[#64748b]'
                                }`}
                            >
                                {micrositeStatus === MICROSITE_STATUS.PUBLISHED
                                    ? 'Live'
                                    : micrositeStatus === MICROSITE_STATUS.DRAFT
                                      ? 'Draft'
                                      : 'Inactive'}
                            </span>
                        </div>
                    )}
                </div>
            </div>

            {/* Form Sections */}
            <div className="space-y-6">
                {/* Step 1: Basic Information */}
                <BasicInfoSection
                    micrositeType={formData.micrositeType}
                    onMicrositeTypeChange={handleMicrositeTypeChange}
                    storeBrandID={formData.storeBrandID}
                    onStoreBrandIDChange={handleStoreBrandIDChange}
                    storeBrandSlug={formData.storeBrandSlug}
                    onStoreBrandSlugChange={handleStoreBrandSlugChange}
                    storeBrandName={formData.storeBrandName}
                    storeID={formData.storeID}
                    onStoreIDChange={handleStoreIDChange}
                    storeSlug={formData.storeSlug}
                    onStoreSlugChange={handleStoreSlugChange}
                    storeName={formData.storeName}
                    isEditMode={isEditMode}
                />

                {/* Step 2: Branding & Content */}
                <BrandingSection
                    tagline={formData.tagline}
                    onTaglineChange={handleTaglineChange}
                    logo={formData.logo}
                    onLogoChange={handleLogoChange}
                    heroBannerTitle={formData.heroBanner?.title}
                    onHeroBannerTitleChange={handleHeroBannerTitleChange}
                    heroBannerSubtitle={formData.heroBanner?.subtitle}
                    onHeroBannerSubtitleChange={handleHeroBannerSubtitleChange}
                    heroBannerHighlightLabel={formData.heroBanner?.highlightLabel}
                    onHeroBannerHighlightLabelChange={handleHeroBannerHighlightLabelChange}
                    heroBannerCtaText={formData.heroBanner?.ctaText}
                    onHeroBannerCtaTextChange={handleHeroBannerCtaTextChange}
                    heroBannerRedirectionUrl={formData.heroBanner?.redirectionUrl}
                    onHeroBannerRedirectionUrlChange={handleHeroBannerRedirectionUrlChange}
                    metaTitle={formData.metaData?.title}
                    onMetaTitleChange={handleMetaTitleChange}
                    metaDescription={formData.metaData?.description}
                    onMetaDescriptionChange={handleMetaDescriptionChange}
                    metaKeywords={formData.metaData?.keywords}
                    onMetaKeywordsChange={handleMetaKeywordsChange}
                    ogImage={formData.metaData?.ogImage}
                    onOgImageChange={handleOgImageChange}
                />

                {/* Step 3: Hero Section */}
                <HeroSection
                    heroImages={formData.hero?.imageUrls}
                    onHeroImagesChange={handleHeroImagesChange}
                    heroTags={formData.hero?.tags}
                    onHeroTagsChange={handleHeroTagsChange}
                />

                {/* Step 4: Location & Contact */}
                <LocationContactSection
                    micrositeType={formData.micrositeType}
                    // Store-specific
                    holidayInfo={formData.location?.holidayInfo}
                    onHolidayInfoChange={handleHolidayInfoChange}
                    latitude={formData.geoLocation?.latitude}
                    onLatitudeChange={handleLatitudeChange}
                    longitude={formData.geoLocation?.longitude}
                    onLongitudeChange={handleLongitudeChange}
                    geoLocationValidity={formData.geoLocation?.validity}
                    onGeoLocationValidityChange={handleGeoLocationValidityChange}
                    storePhone={formData.storeOperationsConfig?.phone}
                    onStorePhoneChange={handleStorePhoneChange}
                    storeWhatsapp={formData.storeOperationsConfig?.whatsappNumber}
                    onStoreWhatsappChange={handleStoreWhatsappChange}
                    timings={formData.storeOperationsConfig?.timings}
                    onTimingsChange={handleTimingsChange}
                    // Brand-specific
                    brandPhone={formData.storeBrandOperationsConfig?.phone}
                    onBrandPhoneChange={handleBrandPhoneChange}
                    brandWhatsapp={formData.storeBrandOperationsConfig?.whatsappNumber}
                    onBrandWhatsappChange={handleBrandWhatsappChange}
                />

                {/* Step 5: Gallery */}
                <GallerySection
                    gallery={formData.gallery?.imageUrls}
                    onChange={handleGalleryChange}
                />

                {/* Step 6: Deals & Offers */}
                <DealsSection
                    dealsEnabled={formData.deals?.items !== undefined}
                    onDealsEnabledChange={handleDealsEnabledChange}
                    deals={formData.deals?.items}
                    onDealsChange={handleDealsChange}
                />

                {/* Step 7: Social Media & Footer */}
                <SocialFooterSection
                    facebookUsername={formData.social?.facebook?.username}
                    onFacebookUsernameChange={handleFacebookUsernameChange}
                    facebookFollowers={formData.social?.facebook?.followers}
                    onFacebookFollowersChange={handleFacebookFollowersChange}
                    instagramUsername={formData.social?.instagram?.username}
                    onInstagramUsernameChange={handleInstagramUsernameChange}
                    instagramFollowers={formData.social?.instagram?.followers}
                    onInstagramFollowersChange={handleInstagramFollowersChange}
                    licenseNumber={formData.footer?.licenseNumber}
                    onLicenseNumberChange={handleLicenseNumberChange}
                    minimumLegalDrinkingAge={formData.footer?.minimumLegalDrinkingAge}
                    onMinimumLegalDrinkingAgeChange={handleMinimumLegalDrinkingAgeChange}
                />

                {/* Step 8: Modal/Popup */}
                <ModalSection
                    modalEnabled={formData.modal !== undefined}
                    onModalEnabledChange={handleModalEnabledChange}
                    headerBadge={formData.modal?.headerBadge}
                    onHeaderBadgeChange={handleModalHeaderBadgeChange}
                    headerTitle={formData.modal?.headerTitle}
                    onHeaderTitleChange={handleModalHeaderTitleChange}
                    headerSubtitle={formData.modal?.headerSubtitle}
                    onHeaderSubtitleChange={handleModalHeaderSubtitleChange}
                    primaryButtonText={formData.modal?.primaryButtonText}
                    onPrimaryButtonTextChange={handleModalPrimaryButtonTextChange}
                    primaryButtonInfo={formData.modal?.primaryButtonInfo}
                    onPrimaryButtonInfoChange={handleModalPrimaryButtonInfoChange}
                    footerNote={formData.modal?.footerNote}
                    onFooterNoteChange={handleModalFooterNoteChange}
                />
            </div>

            {/* Action Buttons */}
            <div className="sticky bottom-0 bg-white border-t border-[#e2e8f0] py-4 mt-8">
                <div className="flex items-center justify-between gap-3">
                    {/* Status Toggle (Edit Mode Only - for Live/Inactive microsites) */}
                    {isEditMode &&
                        (micrositeStatus === MICROSITE_STATUS.PUBLISHED ||
                            micrositeStatus === MICROSITE_STATUS.INACTIVE) && (
                            <button
                                onClick={handleToggleStatus}
                                className={`px-6 py-2.5 border-2 rounded-lg font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                                    micrositeStatus === MICROSITE_STATUS.PUBLISHED
                                        ? 'border-[#ef4444] text-[#ef4444] hover:bg-[#fee2e2]'
                                        : 'border-[#10b981] text-[#10b981] hover:bg-[#dcfce7]'
                                }`}
                                disabled={saveStatus === 'loading'}
                            >
                                {micrositeStatus === MICROSITE_STATUS.PUBLISHED
                                    ? 'Mark as Inactive'
                                    : 'Mark as Live'}
                            </button>
                        )}

                    {/* Main Actions */}
                    <div className="flex items-center gap-3 ml-auto">
                        <button
                            onClick={handleCancel}
                            className="px-6 py-2.5 border border-[#e2e8f0] text-[#64748b] rounded-lg hover:bg-[#f8fafc] transition-colors font-medium"
                            disabled={saveStatus === 'loading'}
                        >
                            Cancel
                        </button>
                        {/* Show Save as Draft for: new microsites OR draft microsites in edit mode */}
                        {(!isEditMode || micrositeStatus === MICROSITE_STATUS.DRAFT) && (
                            <button
                                onClick={handleSaveDraft}
                                className="px-6 py-2.5 border border-[#e2e8f0] text-[#1e293b] rounded-lg hover:bg-[#f8fafc] transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                                disabled={saveStatus === 'loading'}
                            >
                                Save as Draft
                            </button>
                        )}
                        <button
                            onClick={handlePublish}
                            className="px-6 py-2.5 bg-gradient-to-r from-[#6e64f0] to-[#8b7cf6] text-white rounded-lg hover:shadow-lg transition-all font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                            disabled={saveStatus === 'loading'}
                        >
                            {saveStatus === 'loading'
                                ? isEditMode
                                    ? 'Publishing...'
                                    : 'Publishing...'
                                : isEditMode
                                  ? micrositeStatus === MICROSITE_STATUS.DRAFT
                                      ? 'Save & Publish'
                                      : 'Update Microsite'
                                  : 'Save & Publish'}
                        </button>
                    </div>
                </div>
            </div>

            {/* Save Dialog */}
            <SaveDialog
                isOpen={isDialogOpen}
                onClose={handleDialogClose}
                status={saveStatus === 'idle' ? 'loading' : saveStatus}
                successType={successType}
                errorMessage={errorMessage}
                micrositeData={{
                    storeBrandName: formData.storeBrandID ? 'Premium Wines Co.' : undefined,
                    storeName: formData.storeID ? 'Wine & Spirits Emporium' : undefined,
                    url: formData.storeBrandSlug
                        ? `https://${formData.storeBrandSlug}${formData.storeSlug ? '/' + formData.storeSlug : ''}.bottleshop.com`
                        : undefined,
                    type: formData.micrositeType,
                    status: micrositeStatus,
                }}
            />
        </div>
    );
}
